import { Box, Flex, IconButton, Text, useToast } from "@chakra-ui/react";
import {
  LifebuoyIcon,
  MonitorArrowUpIcon,
  SignOutIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LogoutAlertDialog from "../UI/LogoutAlertDialog";
import WarningModal from "../UI/WarningModal";
import { ID_TOKEN_KEY } from "../../helpers/constant";
import { resetOnboarding } from "../../helpers/onboarding";
import {
  buildLayoutTourSteps,
  runOnboardingTour,
} from "../../helpers/onboardingTour";

const SidebarShortcuts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { signOutHandler } = useAuth();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isDashboardWarningOpen, setIsDashboardWarningOpen] = useState(false);
  const [isRestartWarningOpen, setIsRestartWarningOpen] = useState(false);

  const openDashboard = () => {
    window.open(
      `${process.env.REACT_APP_DASHBOARD}?token=${localStorage.getItem(ID_TOKEN_KEY)}`,
      "_blank",
    );
  };

  const restartOnboarding = () => {
    resetOnboarding();
    runOnboardingTour(buildLayoutTourSteps(t), t);
  };

  const handleConfirmLogout = async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);
    try {
      await signOutHandler();
      toast({
        title: String(t("profileMenu.signedOutTitle")),
        description: String(t("profileMenu.signedOutDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      toast({
        title: String(t("profileMenu.signOutErrorTitle")),
        description:
          error instanceof Error ? error.message : String(t("common.tryAgain")),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSigningOut(false);
      setIsLogoutDialogOpen(false);
    }
  };

  return (
    <Flex
      direction="column"
      align="start"
      justify="flex-end"
      h="fit-content"
      gap="12px"
      px={7}
      mb={{ base: "24px", lg: "16px" }}
    >
      <Text color="white" fontSize="12px">
        Shortcuts
      </Text>

      <Flex gap={3}>
        <Box
          bg="gray.100"
          _dark={{ bg: "gray.700" }}
          borderRadius="16px"
          boxShadow="md"
        >
          <IconButton
            id="onboarding-shortcut-dashboard"
            aria-label="Dashboard"
            icon={<MonitorArrowUpIcon size={22} />}
            onClick={() => setIsDashboardWarningOpen(true)}
            borderRadius="full"
            variant="ghost"
          />
        </Box>

        <Box
          bg="gray.100"
          _dark={{ bg: "gray.700" }}
          borderRadius="16px"
          boxShadow="md"
        >
          <IconButton
            id="onboarding-shortcut-restart"
            aria-label={String(t("onboarding.restart.buttonLabel"))}
            icon={<LifebuoyIcon size={22} />}
            borderRadius="full"
            variant="ghost"
            onClick={() => setIsRestartWarningOpen(true)}
          />
        </Box>

        <Box
          bg="gray.100"
          _dark={{ bg: "gray.700" }}
          borderRadius="16px"
          boxShadow="md"
        >
          <IconButton
            id="onboarding-shortcut-logout"
            aria-label="Logout"
            icon={<SignOutIcon size={22} />}
            borderRadius="full"
            variant="ghost"
            onClick={() => setIsLogoutDialogOpen(true)}
          />
        </Box>
      </Flex>

      <LogoutAlertDialog
        isOpen={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        isLoading={isSigningOut}
      />

      <WarningModal
        isOpen={isDashboardWarningOpen}
        onClose={() => setIsDashboardWarningOpen(false)}
        title={String(t("onboarding.shortcuts.dashboardWarningTitle"))}
        description={String(
          t("onboarding.shortcuts.dashboardWarningDescription"),
        )}
        continueLabel={String(t("common.continue"))}
        cancelLabel={String(t("common.cancel"))}
        colorScheme="blue"
        onContinue={openDashboard}
      />

      <WarningModal
        isOpen={isRestartWarningOpen}
        onClose={() => setIsRestartWarningOpen(false)}
        title={String(t("onboarding.shortcuts.restartWarningTitle"))}
        description={String(
          t("onboarding.shortcuts.restartWarningDescription"),
        )}
        continueLabel={String(t("common.continue"))}
        cancelLabel={String(t("common.cancel"))}
        colorScheme="blue"
        onContinue={restartOnboarding}
      />
    </Flex>
  );
};

export default SidebarShortcuts;
